import { CONTACT_INFO_TYPES, type BusinessEntityType, type NetworkNodeData, type ApiNode } from '../types'
import { getNodeTypeConfig } from '../constants'

/** Converts snake_case to Title Case, separating trailing digits (e.g., "country_code" → "Country Code", "address_line1" → "Address Line 1") */
export function formatMetadataKey(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
}

/** Safely stringifies a value, returning a fallback message if JSON.stringify fails */
export function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return '[Unable to display value]'
  }
}

/** Checks if a string is a valid HTTP/HTTPS URL */
export function isUrl(value: string): boolean {
  return /^https?:\/\//.test(value)
}

/** Checks if a node type belongs to the contact info category */
export function isContactInfoType(nodeType: BusinessEntityType): boolean {
  return CONTACT_INFO_TYPES.includes(nodeType as (typeof CONTACT_INFO_TYPES)[number])
}

/** Factory function to create a NetworkNodeData object */
export function createNodeData(
  nodeType: BusinessEntityType,
  identifierKey: string,
  displayName: string,
  metadata?: NetworkNodeData['metadata'],
): NetworkNodeData {
  return {
    nodeType,
    nodeCategory: getNodeTypeConfig(nodeType).category,
    identifierKey,
    displayName,
    ...(metadata && { metadata }),
  }
}

/** Transforms metadata country_code and state to uppercase for the select list matching */
export function transformMetadataForUI(metadata: ApiNode['metadata']): NetworkNodeData['metadata'] {
  if (!metadata) return
  return {
    ...metadata,
    country_code: metadata.country_code?.toUpperCase(),
    state: metadata.state?.toUpperCase(),
  }
}

/** Transforms metadata country_code and state to lowercase for API */
export function transformMetadataForApi(metadata: NetworkNodeData['metadata']): ApiNode['metadata'] {
  if (!metadata) return
  return {
    ...metadata,
    country_code: metadata.country_code?.toLowerCase(),
    state: metadata.state?.toLowerCase(),
  }
}
